import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { onboardingCache, type OnboardingFlagValue } from '../../lib/cache/onboardingCache';
import { onboardingSettingsService } from '../../services/onboardingSettings';
import type { RootState } from '../store';

type SaveStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

const KNOWN_FLAGS = ['importOnboarding', 'importCompletion', 'mix_builder_first_run', 'mix_pref_tooltip'] as const;

export type OnboardingFlag = {
  key: string;
  version: number;
  seenAt: string | null;
  saveStatus: SaveStatus;
  error: string | null;
};

export type OnboardingState = {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  flags: Record<string, OnboardingFlag>;
  lastLoadedUserId: string | null;
  lastHydratedUserId: string | null;
};

const initialState: OnboardingState = {
  status: 'idle',
  error: null,
  flags: {},
  lastLoadedUserId: null,
  lastHydratedUserId: null,
};

const normalizeSeenAt = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length) {
      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.valueOf())) return parsed.toISOString();
    }
  }
  return null;
};

const toFlagMap = (raw: Record<string, OnboardingFlagValue> | null): Record<string, OnboardingFlag> => {
  if (!raw) return {};
  const result: Record<string, OnboardingFlag> = {};
  for (const [key, value] of Object.entries(raw)) {
    const version = typeof value?.version === 'number' && Number.isFinite(value.version) ? value.version : 1;
    const seenAt = normalizeSeenAt(value?.seenAt) ?? null;
    result[key] = { key, version, seenAt, saveStatus: 'idle', error: null };
  }
  return result;
};

const toCachePayload = (flags: Record<string, OnboardingFlag>): Record<string, OnboardingFlagValue> => {
  const payload: Record<string, OnboardingFlagValue> = {};
  for (const [key, value] of Object.entries(flags)) {
    payload[key] = { version: value.version, seenAt: value.seenAt };
  }
  return payload;
};

export const hydrateOnboardingFromCache = createAsyncThunk(
  'onboarding/hydrateFromCache',
  async (userId: string) => {
    const cached = await onboardingCache.load(userId);
    return { userId, flags: toFlagMap(cached) };
  }
);

export const loadOnboardingFlags = createAsyncThunk<
  { userId: string; flags: Record<string, OnboardingFlag> },
  void,
  { state: RootState; rejectValue: string }
>('onboarding/loadOnboardingFlags', async (_arg, { getState, rejectWithValue }) => {
  const userId = getState().auth.user?.id ?? null;
  if (!userId) {
    return rejectWithValue('Missing user context');
  }
  try {
    const flags = await onboardingSettingsService.fetchAll();
    const normalized = toFlagMap(flags);
    for (const key of KNOWN_FLAGS) {
      if (!normalized[key]) {
        normalized[key] = { key, version: 1, seenAt: null, saveStatus: 'idle', error: null };
      }
    }
    await onboardingCache.persist(userId, toCachePayload(normalized));
    return { userId, flags: normalized };
  } catch (error: any) {
    const message = error?.response?.data?.error ?? error?.message ?? 'Failed to load onboarding flags';
    return rejectWithValue(message);
  }
}, {
  condition: (_arg, { getState }) => {
    const state = getState() as RootState;
    const userId = state.auth.user?.id ?? null;
    if (!userId) return false;
    const { lastLoadedUserId, status } = state.onboarding;
    const alreadyLoaded =
      lastLoadedUserId === userId &&
      (status === 'succeeded' || status === 'failed');
    return !alreadyLoaded;
  }
});

export const markOnboardingSeen = createAsyncThunk<
  { userId: string; key: string; seenAt: string; version: number },
  { key: string; version?: number; seenAt?: string | null },
  { state: RootState; rejectValue: string }
>('onboarding/markSeen', async (payload, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const userId = state.auth.user?.id ?? null;
  if (!userId) {
    return rejectWithValue('Missing user context');
  }
  const key = payload.key.trim();
  if (!key) return rejectWithValue('Missing flag key');
  const seenAt = normalizeSeenAt(payload.seenAt) ?? new Date().toISOString();
  const version = typeof payload.version === 'number' && Number.isFinite(payload.version) ? payload.version : 1;

  dispatch(onboardingSlice.actions.applyLocalSeen({ key, seenAt, version }));

  const afterLocal = (getState() as RootState).onboarding.flags;
  await onboardingCache.persist(userId, toCachePayload(afterLocal));

  try {
    await onboardingSettingsService.markSeen(key, { version, seenAt });
    await onboardingCache.persist(userId, toCachePayload((getState() as RootState).onboarding.flags));
    return { userId, key, seenAt, version };
  } catch (error: any) {
    const message = error?.response?.data?.error ?? error?.message ?? 'Failed to mark onboarding seen';
    return rejectWithValue(message);
  }
});

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    applyLocalSeen(state, action: PayloadAction<{ key: string; seenAt: string; version: number }>) {
      const { key, seenAt, version } = action.payload;
      const existing = state.flags[key] ?? { key, version, seenAt: null, saveStatus: 'idle', error: null };
      state.flags[key] = {
        ...existing,
        version,
        seenAt,
        saveStatus: 'pending',
        error: null,
      };
    },
    resetForUser(state) {
      state.flags = {};
      state.status = 'idle';
      state.error = null;
      state.lastLoadedUserId = null;
      state.lastHydratedUserId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateOnboardingFromCache.fulfilled, (state, action) => {
        if (state.lastHydratedUserId && state.lastHydratedUserId !== action.payload.userId) {
          // user switched; reset
          state.flags = {};
        }
        state.flags = { ...state.flags, ...action.payload.flags };
        state.lastHydratedUserId = action.payload.userId;
      })
      .addCase(loadOnboardingFlags.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadOnboardingFlags.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        // Replace with server truth to allow clearing/resetting flags server-side.
        state.flags = { ...action.payload.flags };
        state.lastLoadedUserId = action.payload.userId;
      })
      .addCase(loadOnboardingFlags.rejected, (state, action) => {
        state.status = 'failed';
        state.error = typeof action.payload === 'string' ? action.payload : action.error?.message ?? 'Failed to load onboarding flags';
      })
      .addCase(markOnboardingSeen.pending, (state, action) => {
        const { key, seenAt, version } = action.meta.arg;
        const resolvedSeenAt = normalizeSeenAt(seenAt) ?? new Date().toISOString();
        const resolvedVersion = typeof version === 'number' && Number.isFinite(version) ? version : 1;
        const existing = state.flags[key] ?? { key, version: resolvedVersion, seenAt: null, saveStatus: 'idle', error: null };
        state.flags[key] = {
          ...existing,
          version: resolvedVersion,
          seenAt: resolvedSeenAt,
          saveStatus: 'pending',
          error: null,
        };
      })
      .addCase(markOnboardingSeen.fulfilled, (state, action) => {
      const { key, seenAt, version } = action.payload;
      const existing = state.flags[key] ?? { key, version, seenAt: null, saveStatus: 'idle', error: null };
      state.flags[key] = {
        ...existing,
        version,
        seenAt,
        saveStatus: 'succeeded',
        error: null,
      };
      state.lastLoadedUserId = action.payload.userId ?? state.lastLoadedUserId;
    })
      .addCase(markOnboardingSeen.rejected, (state, action) => {
        const key = action.meta.arg.key;
        const existing = state.flags[key];
        if (existing) {
          state.flags[key] = {
            ...existing,
            saveStatus: 'failed',
            error:
              typeof action.payload === 'string'
                ? action.payload
                : action.error?.message ?? 'Failed to mark onboarding seen',
          };
        }
      });
  },
});

export const onboardingActions = {
  ...onboardingSlice.actions,
  hydrateOnboardingFromCache,
  loadOnboardingFlags,
  markOnboardingSeen,
};

export const selectOnboardingFlag = (state: RootState, key: string) => state.onboarding.flags[key] ?? null;

export default onboardingSlice.reducer;
